import React, {useState, useEffect} from 'react';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {apiClient} from '@/shared/services';
import {useAppSelector, useAppDispatch} from '@/shared/hooks/redux';
import {useTestsState, showDataLoadError} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {selectCurrentPatient, restoreCurrentPatientFromStorage} from '@/slices/patientSlice';
import {useDashboardData} from '@/modules/patient-dashboard/hooks/useDashboardData';
import {getCategoryStats} from '@/modules/patient-dashboard/utils/healthCategories';
import {useIsDashboardModuleEnabled} from '@/modules/patient-dashboard/hooks/useDashboardModules';
import Icon from '@/shared/components/Icon';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageHeader from '@/shared/layout/PageHeader';
import SectionHeader from '@/shared/components/SectionHeader';
import {getLaunchContext} from '@/shared/utils/tokenUtils';
import HealthOverview from './components/HealthOverview';
import LabTrends from './components/LabTrends';
import LabResults from './components/LabResults';
import CustomBioMatchHero from '@/modules/biomatch/components/CustomBioMatchHero';
import HealthProfileCard from './components/HealthProfileCard';
import YourLatestOrderCard from './components/YourLatestOrderCard';
import MyLabsPlusCard from '@/modules/patient-dashboard/components/MyLabsPlusCard';
import {useHasBioMatchCard} from '@/modules/biomatch/hooks/biomatchCardVisibility';
import BiomarkersNeedAttentionCard from '@/modules/patient-dashboard/components/BiomarkersNeedAttentionCard';
import LoadingSpinner from '@/shared/components/LoadingSpinner';

const Dashboard: React.FC = () => {
    const {user} = useAuth();
    const dispatch = useAppDispatch();
    const testsState = useTestsState();
    const currentPatient = useAppSelector(selectCurrentPatient);

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: currentPatientData, isSubscribed} = useDashboardData();

    // Track selected health category
    const [selectedCategory, setSelectedCategory] = useState<string>('heart');
    const [hasInitializedCategory, setHasInitializedCategory] = useState<boolean>(false);

    // Track selected biomarker for synchronization between LabResults and LabTrends
    const [selectedBiomarkerName, setSelectedBiomarkerName] = useState<string | null>(null);

    // Update selected category to first available category with data when data loads (only once)
    useEffect(() => {
        if (currentPatientData.length > 0 && !hasInitializedCategory) {
            const allResults = currentPatientData.flatMap(
                (test: any) =>
                    test.results?.map((result: any) => ({
                        ...result,
                        createdDate: test.createdDate,
                    })) || []
            );

            const categoryStats = getCategoryStats(allResults);

            // Filter and sort categories the same way as HorizontalCategoryChart to get the true "first" category
            const availableCategories = categoryStats
                .filter((category) => category.availableTests > 0)
                .sort((a, b) => b.averageValue - a.averageValue); // Sort by score descending (same as chart)

            const firstAvailableCategory = availableCategories[0];

            if (firstAvailableCategory) {
                setSelectedCategory(firstAvailableCategory.key);
                setHasInitializedCategory(true);
            }
        }
    }, [currentPatientData, hasInitializedCategory]);

    // Ensure API client has the latest token before making requests
    React.useEffect(() => {
        const initializeToken = async () => {
            if (user?.token) {
                apiClient.setToken(user.token);
            } else {
                // Try to refresh from localStorage
                apiClient.refreshTokenFromStorage();
            }
        };

        initializeToken();
    }, [user?.token]);

    // Restore current patient from localStorage on page load (handles token refresh scenarios)
    React.useEffect(() => {
        if ((!currentPatient?.id || currentPatient.id === 0) && user?.token) {
            dispatch(restoreCurrentPatientFromStorage());
        }
    }, [dispatch, currentPatient?.id, user?.token]);

    // Surface a real load failure — but only when there's nothing cached to
    // render (a failed background refetch over visible data stays silent).
    const error = showDataLoadError(testsState.isError, testsState.tests.length > 0);

    // Get current hour for time-based greeting
    const currentHour = new Date().getHours();
    const getGreeting = () => {
        if (currentHour < 12) return 'Good Morning';
        if (currentHour < 17) return 'Good Afternoon';
        return 'Good Evening';
    };

    // Get user's first name from current patient (set during login) or auth user
    // Normalize to capitalize only first letter, rest lowercase
    const rawUserName = currentPatient?.firstName || user?.userInfo?.firstName || 'User';
    const userName = rawUserName.charAt(0).toUpperCase() + rawUserName.slice(1).toLowerCase();
    const greeting = `${getGreeting()}, ${userName}`;

    // OHD-launched sessions get a "Reschedule" CTA next to the greeting.
    // Hidden when the session didn't originate from an OHD launch (the
    // claim isn't on the JWT) or when no return URL is configured. Always
    // visible on this view for OHD-launched patients per UX.
    const launchContext = getLaunchContext(user?.token);
    const rescheduleHref = launchContext?.source === 'ohd' && launchContext.returnUrl ? launchContext.returnUrl : null;
    const rescheduleAction = rescheduleHref ? (
        <a
            href={rescheduleHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-mld-primary px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-mld-primary/90 transition-colors">
            <Icon name="calendar-clock" className="h-4 w-4" />
            Reschedule
        </a>
    ) : null;

    // Module toggle system — not loaded yet means "show all" (zero regression)
    const isModuleEnabled = useIsDashboardModuleEnabled();

    // The membership upsell only appears beside an actual kit offer
    // (Derrick, 2026-08-11): no BioMatch card on this dashboard → no
    // My Labs Plus card either. Shares the hero's exact visibility
    // predicate so the two can never disagree. Members don't get the
    // upsell — that check is lifted out of MyLabsPlusCard (which still
    // self-guards) because the sidebar needs to know whether the card's
    // SectionHeader actually renders to keep the columns aligned.
    const hasBioMatchCard = useHasBioMatchCard();
    const showMembershipCard =
        isModuleEnabled('patient.mylabs_plus') &&
        isModuleEnabled('patient.recommendations') &&
        hasBioMatchCard &&
        !isSubscribed;

    // Show loading spinner while data is being fetched
    if (testsState.loading) {
        return (
            <DashboardLayout>
                <LoadingSpinner />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="relative">
                <div>
                    <PageHeader title={greeting} rightAction={rescheduleAction} />

                    {error && (
                        <div className="bg-mld-danger/5 border border-mld-danger/20 rounded-lg p-4 mb-6">
                            <p className="text-mld-danger">
                                Unable to load your health data. Please try refreshing the page.
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_clamp(25rem,28vw,31.5rem)] gap-6 xl:gap-8">
                        <div className="min-w-0 space-y-6">
                            {/* Mobile: main content + BioMatch cards rendered
                                inline (the v5 right sidebar collapses below
                                the main column on narrow viewports). */}
                            <div className="xl:hidden space-y-6">
                                {isModuleEnabled('patient.health_profile') && <HealthProfileCard />}
                                {isModuleEnabled('patient.latest_order') && <YourLatestOrderCard />}
                                {isModuleEnabled('patient.health_overview') && (
                                    <HealthOverview
                                        selectedCategory={selectedCategory}
                                        onCategoryChange={setSelectedCategory}
                                        onBiomarkerSelect={setSelectedBiomarkerName}
                                        selectedBiomarkerName={selectedBiomarkerName}
                                    />
                                )}
                                {isModuleEnabled('patient.lab_trends') && (
                                    <LabTrends
                                        selectedCategory={selectedCategory}
                                        onTrendSelect={setSelectedBiomarkerName}
                                    />
                                )}
                                {isModuleEnabled('patient.lab_results') && (
                                    <LabResults
                                        selectedBiomarkerName={selectedBiomarkerName}
                                        onBiomarkerChange={setSelectedBiomarkerName}
                                        onCategoryChange={setSelectedCategory}
                                    />
                                )}
                                {showMembershipCard && <MyLabsPlusCard isSubscribed={isSubscribed} />}
                                {isModuleEnabled('patient.recommendations') && (
                                    <>
                                        <BiomarkersNeedAttentionCard />
                                        <CustomBioMatchHero />
                                    </>
                                )}
                            </div>

                            {/* Desktop (xl+): main content only — the BioMatch
                                cards move to the fluid right sidebar. */}
                            <div className="hidden xl:block space-y-6">
                                {isModuleEnabled('patient.health_profile') && <HealthProfileCard />}
                                {isModuleEnabled('patient.latest_order') && <YourLatestOrderCard />}
                                {isModuleEnabled('patient.health_overview') && (
                                    <HealthOverview
                                        selectedCategory={selectedCategory}
                                        onCategoryChange={setSelectedCategory}
                                        onBiomarkerSelect={setSelectedBiomarkerName}
                                        selectedBiomarkerName={selectedBiomarkerName}
                                    />
                                )}
                                {isModuleEnabled('patient.lab_trends') && (
                                    <LabTrends
                                        selectedCategory={selectedCategory}
                                        onTrendSelect={setSelectedBiomarkerName}
                                    />
                                )}
                                {isModuleEnabled('patient.lab_results') && (
                                    <LabResults
                                        selectedBiomarkerName={selectedBiomarkerName}
                                        onBiomarkerChange={setSelectedBiomarkerName}
                                        onCategoryChange={setSelectedCategory}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Fluid right sidebar (xl+) — stacks the two BioMatch
                            cards (Your BioMatch Kit + BioMatched For You). Width
                            scales with the viewport via the grid template so the
                            card breathes toward the PSD's proportions on large
                            monitors and stacks full-width below xl. */}
                        <div className="hidden xl:block space-y-6">
                            {showMembershipCard && <MyLabsPlusCard isSubscribed={isSubscribed} />}
                            {isModuleEnabled('patient.recommendations') && (
                                <section>
                                    {/* The left column's first card sits below its own
                                        SectionHeader (inside HealthProfileCard). When the
                                        membership card — the only sidebar item with an
                                        external header — is hidden, this invisible header
                                        keeps the sidebar's first card on the same row
                                        plane. `invisible` preserves layout height and
                                        drops it from the accessibility tree. */}
                                    {!showMembershipCard && (
                                        <SectionHeader title="My Labs Plus" className="invisible" />
                                    )}
                                    <div className="space-y-6">
                                        <BiomarkersNeedAttentionCard />
                                        <CustomBioMatchHero />
                                    </div>
                                </section>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Dashboard;
